import * as React from 'react';
import { Modal as MuiModal } from '@material-ui/core';
import { IconX } from '@tabler/icons';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '#root/lib/utils';
import styles from './modal.module.scss';

/**
 * Variantes de modal usando class-variance-authority + SCSS modules
 */
const modalVariants = cva(styles.surface, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

/**
 * Tono del ícono del header (sin tono: color de marca)
 */
const modalIconVariants = cva(styles.icon, {
  variants: {
    tone: {
      brand: '',
      info: styles.toneInfo,
      success: styles.toneSuccess,
      warning: styles.toneWarning,
      error: styles.toneError,
      match: styles.toneMatch,
    },
  },
  defaultVariants: {
    tone: 'brand',
  },
});

export type ModalCloseReason = 'backdropClick' | 'escapeKeyDown' | 'closeButton';

/**
 * Red de seguridad si no llega `animationend` (pestaña en segundo plano, etc.).
 * Debe superar la salida más larga (--ds-modal-duration-exit: 300ms en mobile).
 */
const EXIT_FALLBACK_MS = 600;

interface ModalContextValue {
  titleId: string;
  descriptionId: string;
  hasClose: boolean;
  setDescribed: (described: boolean) => void;
}

const ModalContext = React.createContext<ModalContextValue>({
  titleId: undefined,
  descriptionId: undefined,
  hasClose: false,
  setDescribed: () => {},
});

/**
 * Fondo del modal (BackdropComponent de MUI). MUI le pasa `open` y `onClick`; sigue montado
 * durante la salida para animarse junto con la superficie. Sin JSS.
 */
interface ModalBackdropProps extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
}

const ModalBackdrop = ({ open, className, ...props }: ModalBackdropProps) => (
  <div
    aria-hidden="true"
    className={cn(styles.backdrop, className)}
    data-state={open ? 'open' : 'closed'}
    {...props}
  />
);

/**
 * Superficie de vidrio. Es el hijo DIRECTO de MuiModal: la prop `in` hace que MUI la trate
 * como transición y le inyecte `onEnter`/`onExited`. Contrato:
 * - `onEnter` al abrir, también en el primer montaje: sin él MUI la desmonta sin animar.
 * - `onExited` al terminar la salida: con `closeAfterTransition`, recién ahí MUI quita el
 *   bloqueo de scroll y el aria-hidden de la app. Por eso hay timer de respaldo.
 */
interface ModalSurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  in: boolean;
  onEnter?: () => void;
  onExited?: () => void;
}

const ModalSurface = React.forwardRef<HTMLDivElement, ModalSurfaceProps>(
  ({ in: open, onEnter, onExited, onAnimationEnd, ...props }, ref) => {
    // MUI recrea los callbacks en cada render: se leen de un ref para no reiniciar el timer
    const callbacks = React.useRef({ onEnter, onExited });
    React.useLayoutEffect(() => {
      callbacks.current = { onEnter, onExited };
    });

    const exitedRef = React.useRef(false);
    const finishExit = React.useCallback(() => {
      if (exitedRef.current) return;
      exitedRef.current = true;
      callbacks.current.onExited?.();
    }, []);

    React.useLayoutEffect(() => {
      exitedRef.current = false;
      if (open) {
        callbacks.current.onEnter?.();
        return undefined;
      }
      const timer = window.setTimeout(finishExit, EXIT_FALLBACK_MS);
      // Se limpia si el modal se reabre a mitad de la salida
      return () => window.clearTimeout(timer);
    }, [open, finishExit]);

    const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
      onAnimationEnd?.(event);
      // Solo la animación de salida de la propia superficie, no la de sus hijos
      if (!open && event.target === event.currentTarget) finishExit();
    };

    return (
      <div
        ref={ref}
        data-state={open ? 'open' : 'closed'}
        onAnimationEnd={handleAnimationEnd}
        {...props}
      />
    );
  }
);
ModalSurface.displayName = 'ModalSurface';

export interface ModalProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof modalVariants> {
  /**
   * Si true, el modal está abierto.
   * Mantén `<Modal>` montado y cambia `open`: con `{open && <Modal />}` no anima la salida.
   */
  open: boolean;
  /**
   * Pedido de cierre: clic en el fondo, Escape o la X
   */
  onClose?: (reason: ModalCloseReason) => void;
  /**
   * Oculta la X de cierre
   */
  hideClose?: boolean;
  /**
   * Ignora el clic en el fondo (Escape y la X siguen cerrando)
   */
  disableBackdropClose?: boolean;
  /**
   * Texto accesible de la X
   * @default "Cerrar"
   */
  closeLabel?: string;
}

/**
 * Modal Component - Design System
 *
 * Diálogo sobre vidrio (liquid glass); en mobile, bottom sheet flotante.
 * MUI Modal pone el comportamiento (portal, foco atrapado, Escape, bloqueo de scroll y
 * apilado con los Dialog legacy); el DOM, el vidrio y las animaciones son del DS.
 *
 * - Sin `ModalTitle`, pasa `aria-label`.
 * - Para enfocar un campo al abrir, usa `autoFocus` en ese campo.
 *
 * @example
 * <Modal open={open} onClose={closeDialog}>
 *   <ModalHeader icon={<IconTruckDelivery />}>
 *     <ModalTitle>Configurar envío</ModalTitle>
 *     <ModalDescription>Elige cómo recibir tu pedido.</ModalDescription>
 *   </ModalHeader>
 *   <ModalBody>Contenido...</ModalBody>
 *   <ModalFooter>
 *     <Button variant="ghost" onClick={closeDialog}>Cancelar</Button>
 *     <Button onClick={handleSave}>Guardar</Button>
 *   </ModalFooter>
 * </Modal>
 *
 * @example
 * // Confirmación compacta, sin cierre por clic afuera
 * <Modal open={open} onClose={closeDialog} size="sm" disableBackdropClose>...</Modal>
 */
const Modal = React.forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      open,
      onClose,
      size,
      hideClose = false,
      disableBackdropClose = false,
      closeLabel = 'Cerrar',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const titleId = React.useId();
    const descriptionId = React.useId();
    const [described, setDescribed] = React.useState(false);

    const context = React.useMemo(
      () => ({ titleId, descriptionId, hasClose: !hideClose, setDescribed }),
      [titleId, descriptionId, hideClose]
    );

    // Se filtra por reason en vez de usar disableBackdropClick de MUI (deprecado en 4.12)
    const handleMuiClose = (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && disableBackdropClose) return;
      onClose?.(reason);
    };

    return (
      <MuiModal
        open={open}
        onClose={handleMuiClose}
        closeAfterTransition
        BackdropComponent={ModalBackdrop}
        className={styles.root}>
        <ModalSurface
          ref={ref}
          in={open}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={described ? descriptionId : undefined}
          className={cn(modalVariants({ size }), className)}
          {...props}>
          <ModalContext.Provider value={context}>{children}</ModalContext.Provider>
          {/* Al final del DOM: el primer Tab va al contenido, no a la X */}
          {!hideClose && (
            <button
              type="button"
              className={styles.close}
              aria-label={closeLabel}
              onClick={() => onClose?.('closeButton')}>
              <IconX size={18} stroke={2} aria-hidden="true" />
            </button>
          )}
        </ModalSurface>
      </MuiModal>
    );
  }
);
Modal.displayName = 'Modal';

/**
 * ModalHeader - Ícono opcional + títulos. Reserva el espacio de la X.
 * `tone` pinta el ícono: brand (default), info, success, warning, error o match.
 *
 * @example <ModalHeader icon={<IconInfoCircle />} tone="info">...</ModalHeader>
 */
export interface ModalHeaderProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof modalIconVariants> {
  /**
   * Ícono destacado a la izquierda de los títulos
   * @example <ModalHeader icon={<IconTruckDelivery />}>...</ModalHeader>
   */
  icon?: React.ReactNode;
}

const ModalHeader = React.forwardRef<HTMLDivElement, ModalHeaderProps>(
  ({ className, icon, tone, children, ...props }, ref) => {
    const { hasClose } = React.useContext(ModalContext);

    return (
      <div
        ref={ref}
        className={cn(styles.header, hasClose && styles.headerWithClose, className)}
        {...props}>
        {icon && (
          <span className={modalIconVariants({ tone })} aria-hidden="true">
            {icon}
          </span>
        )}
        <div className={styles.headings}>{children}</div>
      </div>
    );
  }
);
ModalHeader.displayName = 'ModalHeader';

/**
 * ModalTitle - Título del modal (nombre accesible del diálogo)
 */
export interface ModalTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Nivel del heading (h1-h6)
   * @default "h2"
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const ModalTitle = React.forwardRef<HTMLHeadingElement, ModalTitleProps>(
  ({ className, as: Component = 'h2', ...props }, ref) => {
    const { titleId } = React.useContext(ModalContext);

    return <Component ref={ref} id={titleId} className={cn(styles.title, className)} {...props} />;
  }
);
ModalTitle.displayName = 'ModalTitle';

/**
 * ModalDescription - Texto de apoyo bajo el título (descripción accesible del diálogo)
 */
export interface ModalDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {}

const ModalDescription = React.forwardRef<HTMLParagraphElement, ModalDescriptionProps>(
  ({ className, ...props }, ref) => {
    const { descriptionId, setDescribed } = React.useContext(ModalContext);

    // Se registra para que aria-describedby solo apunte a un id que existe
    React.useLayoutEffect(() => {
      setDescribed(true);
      return () => setDescribed(false);
    }, [setDescribed]);

    return (
      <p ref={ref} id={descriptionId} className={cn(styles.description, className)} {...props} />
    );
  }
);
ModalDescription.displayName = 'ModalDescription';

/**
 * ModalBody - Contenido. Es la única zona con scroll: header y footer quedan fijos.
 */
export interface ModalBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Sin padding lateral: el contenido va de borde a borde (ej. una tabla)
   */
  flush?: boolean;
}

const ModalBody = React.forwardRef<HTMLDivElement, ModalBodyProps>(
  ({ className, flush = false, ...props }, ref) => (
    <div ref={ref} className={cn(styles.body, flush && styles.bodyFlush, className)} {...props} />
  )
);
ModalBody.displayName = 'ModalBody';

/**
 * ModalFooter - Acciones. A la derecha en desktop; apiladas en mobile (la última arriba).
 */
export interface ModalFooterProps extends React.HTMLAttributes<HTMLDivElement> {}

const ModalFooter = React.forwardRef<HTMLDivElement, ModalFooterProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn(styles.footer, className)} {...props} />
  )
);
ModalFooter.displayName = 'ModalFooter';

export {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
  modalVariants,
};
